import * as React from 'react'
import { type MouseEvent, useMemo } from 'react'
import MoreVert from '@mui/icons-material/MoreVert'
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-pro'

import {
  formattedAddress,
  getScheduledTime,
  ServiceLocationLink,
} from '../columnHelpers'
import type { RowType } from '../typings'
import Link from '@mui/material/Link'
import { useAppSelector } from '@user/store/hooks'
import { selectCurrentUser } from '@user/store/authorization'
import { pestPacOrderUrl } from '@user/components/Scheduling/constants'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'

type Props = {
  rowMenuClick: (e: MouseEvent<HTMLElement>, row: RowType) => void
}

export function useColumns(props: Props) {
  const { rowMenuClick } = props

  const { clientUrl } = useAppSelector(selectCurrentUser)

  const columns = useMemo<GridColDef<RowType>[]>(
    () => [
      {
        field: 'actions',
        headerName: '',
        type: 'actions',
        width: 52,
        sortable: false,
        resizable: false,
        filterable: false,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="more"
            label="more"
            icon={<MoreVert />}
            onClick={(e) => rowMenuClick(e, row)}
            data-test-id={`appointmentRowMoreBtn-${row.appointmentId}`}
          />,
        ],
      },
      {
        field: 'workTime',
        headerName: 'Appointment Date',
        type: 'dateTime',
        minWidth: 150,
        sortable: false,
        filterable: false,
        valueGetter: getScheduledTime,
        renderCell: ({ formattedValue }) =>
          formattedValue === null ? <>&mdash;</> : formattedValue,
      },
      {
        field: 'tech',
        headerName: 'Tech / Sales',
        minWidth: 120,
        sortable: false,
        filterable: false,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: 'appointmentNumber',
        headerName: 'Order #',
        width: 75,
        minWidth: 75,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const orderUrl = `${clientUrl}/${pestPacOrderUrl}${row.appointmentId}`
          const locationUrl = `${clientUrl}/location/detail.asp?LocationID=${row.locationId}`
          const href = row.status !== 'Invoiced' ? orderUrl : locationUrl
          return (
            <Link href={href} target="_blank">
              {row.appointmentNumber}
            </Link>
          )
        },
      },
      {
        field: 'serviceLocationAddress',
        headerName: 'Address',
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.serviceLocationAddress === null) return <>&mdash;</>

          const addressText = formattedAddress(row)

          return (
            <AddressMapLink
              address={addressText}
              entityId={row.entityId}
              entityType={row.entityType}
              serviceLocationId={
                row.serviceLocationId ||
                row.serviceLocationAddress?.serviceLocationId
              }
              latitude={row.serviceLocationAddress?.latitude}
              longitude={row.serviceLocationAddress?.longitude}
            >
              {addressText}
            </AddressMapLink>
          )
        },
      },
      {
        field: 'serviceLocation',
        headerName: 'Svc. Location',
        minWidth: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const { locationId, locationCode } = row

          if (locationId === null || locationCode === null) return <>&mdash;</>

          return (
            <ServiceLocationLink
              locationId={locationId}
              locationCode={locationCode}
            />
          )
        },
      },
      {
        field: 'appointmentType',
        headerName: 'Appt. Type',
        width: 90,
        minWidth: 90,
        sortable: false,
        filterable: false,
        valueGetter: ({ row }) => {
          const { appointmentType, billingType, locationId } = row

          // appt was cancelled via SC, appointment type not valid
          if (locationId === null) return null

          if (appointmentType === 'Estimate') {
            return `${billingType} ${appointmentType}`
          } else {
            return 'Service'
          }
        },
        renderCell: ({ value }) => (value === null ? <>&mdash;</> : value),
      },

      {
        field: 'scheduledBy',
        headerName: 'Scheduled By',
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => (value === null ? <>&mdash;</> : value),
      },
      {
        field: 'scheduledTime',
        headerName: 'Scheduled Date',
        type: 'dateTime',
        minWidth: 150,
        sortable: false,
        filterable: false,
        valueGetter: getScheduledTime,
        renderCell: ({ formattedValue }) =>
          formattedValue === null ? <>&mdash;</> : formattedValue,
      },
    ],
    [rowMenuClick, clientUrl],
  )

  return columns
}
